/**
 * Today at a glance.
 *
 * Six numbers, and each one is a question the owner cannot answer today without ringing
 * somebody: who is working, what is unassigned, what did we collect, who never paid, and what
 * needs him personally. No charts — a chart of six numbers is decoration.
 */

import { useMemo } from "react";
import { useNavigate } from "react-router";
import { JOB_STATE_AR, JOB_STATES } from "@fanni/shared/job-state";
import type { JobState } from "@fanni/shared/job-state";
import { formatIQD } from "@fanni/shared/money";
import { ACCESS, useAuth } from "../lib/auth.js";
import { STATUS_TONE, today } from "../lib/format.js";
import { useBoard, usePayments, useStats } from "../lib/queries.js";
import { Badge, Card, ErrorNote, Loading, Note, PageHeader, Stat } from "../ui/kit.js";

/** The states that mean a technician is out there with the job open right now. */
const ACTIVE_STATES: JobState[] = ["en_route", "on_site", "in_progress"];

export function Dashboard() {
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const money = can(...ACCESS.money);
  const conflicts = can(...ACCESS.conflicts);

  const stats = useStats(30);
  const board = useBoard(today());
  const payments = usePayments();

  const activeTechnicians = useMemo(() => {
    if (!board.data) return 0;
    return board.data.columns.filter((c) => c.jobs.some((j) => ACTIVE_STATES.includes(j.status)))
      .length;
  }, [board.data]);

  /**
   * Collected today, computed here rather than asked for: /dashboard/stats has no
   * cash-collected figure and /money/payments returns the last 500 ordered by receipt, which
   * covers a day for a 50-technician company several times over. If a company ever outgrows
   * that this becomes wrong quietly, so it wants a server-side figure eventually.
   */
  const collectedToday = useMemo(() => {
    if (!payments.data) return null;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return payments.data.payments
      .filter((p) => new Date(p.receivedAt) >= start)
      .reduce((a, p) => a + p.amountFils, 0);
  }, [payments.data]);

  const byStatus = stats.data?.jobs_by_status ?? {};
  const unassigned = board.data?.unassigned.length ?? 0;

  return (
    <>
      <PageHeader title={`أهلاً، ${user?.name ?? ""}`} subtitle="حالة الشركة اليوم." />

      {stats.error ? <ErrorNote error={stats.error} className="mb-4" /> : null}

      {stats.isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
            <Stat
              label="فنيون يعملون الآن"
              value={<span className="num">{activeTechnicians}</span>}
              hint="في الطريق أو في الموقع أو قيد التنفيذ"
              tone={activeTechnicians > 0 ? "good" : "neutral"}
              onClick={() => navigate("/board")}
            />
            <Stat
              label="مهام غير مُسندة اليوم"
              value={<span className="num">{unassigned}</span>}
              hint={unassigned > 0 ? "تحتاج موزّعاً" : "كلها مُسندة"}
              tone={unassigned > 0 ? "warn" : "good"}
              onClick={() => navigate("/board")}
            />
            {conflicts ? (
              <Stat
                label="تعارضات تحتاجك"
                value={<span className="num">{stats.data?.conflicts_unresolved ?? 0}</span>}
                hint="قرار عمل، لا قرار بيانات"
                tone={(stats.data?.conflicts_unresolved ?? 0) > 0 ? "bad" : "good"}
                onClick={() => navigate("/conflicts")}
              />
            ) : null}
            {money ? (
              <>
                <Stat
                  label="المقبوض اليوم"
                  value={
                    <span className="num">
                      {collectedToday === null ? "—" : formatIQD(collectedToday)}
                    </span>
                  }
                  tone="good"
                  onClick={() => navigate("/payments")}
                />
                {/*
                  Two cards, two different questions, and they are never merged. "مستحقة" is
                  everything issued and uncollected — a healthy company always carries some.
                  "متأخرة" is the part that blew its agreed date, and it is the owner's third
                  question. Conflating them is precisely how this card used to read 0.
                */}
                <Stat
                  label="متأخرة عن موعدها"
                  value={<span className="num">{stats.data?.overdue_display ?? "—"}</span>}
                  hint={
                    stats.data ? `${stats.data.overdue_count} فاتورة تجاوزت تاريخ استحقاقها` : undefined
                  }
                  tone={(stats.data?.overdue_fils ?? 0) > 0 ? "bad" : "good"}
                  onClick={() => navigate("/invoices?view=overdue")}
                />
                <Stat
                  label="إجمالي المستحق"
                  value={<span className="num">{stats.data?.receivable_display ?? "—"}</span>}
                  hint="كل ما صدر ولم يُحصَّل — متأخراً كان أو ضمن موعده"
                  tone="warn"
                  onClick={() => navigate("/invoices?view=uncollected")}
                />
              </>
            ) : null}
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card title="المهام حسب الحالة" className="lg:col-span-2">
              <div className="flex flex-wrap gap-2">
                {JOB_STATES.filter((s) => (byStatus[s] ?? 0) > 0).map((s) => (
                  <button
                    key={s}
                    onClick={() => navigate(`/jobs?status=${s}`)}
                    className="flex items-center gap-2 rounded-md border border-line px-3 py-2 hover:border-accent/40"
                  >
                    <Badge tone={STATUS_TONE[s]}>{JOB_STATE_AR[s]}</Badge>
                    <span className="num text-sm font-bold">{byStatus[s]}</span>
                  </button>
                ))}
                {JOB_STATES.every((s) => (byStatus[s] ?? 0) === 0) ? (
                  <p className="text-sm text-ink-faint">لا توجد مهام بعد.</p>
                ) : null}
              </div>
            </Card>

            <Card title="طابور الصور">
              <p className="num text-2xl font-bold text-ink">{stats.data?.media_pending ?? 0}</p>
              {/* SYNC.md Rule 3: a queue depth, not an alert. It is a problem only if it stalls. */}
              <Note className="mt-3">
                صورة لم تصل بعد من أجهزة الفنيين. هذا رقم طبيعي: الصورة تُرفع على طابور منفصل وقد
                تستغرق ساعات، والمهمة تُقفل من دونها. يصبح مشكلة فقط إذا توقّف عن النزول.
              </Note>
            </Card>
          </div>
        </>
      )}
    </>
  );
}
